import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, 
  Loader2, 
  CheckCircle, 
  Phone, 
  Wallet, 
  ExternalLink, 
  ArrowLeft,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from "@/lib/utils";
import { useAuth } from '@/contexts/AuthContext';
import { Edit, BarChart3 } from 'lucide-react';

interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  required: boolean;
  options?: string[];
}

interface FormData {
  id: string;
  title: string;
  description: string;
  fields: FormField[];
  logoUrl?: string;
  brandColor?: string;
  userId?: string;
}

const FormView = () => {
  const { formId } = useParams();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<FormData | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [currentSubmissionId, setCurrentSubmissionId] = useState<string | null>(null);
  
  const { user } = useAuth();
  const isOwner = user && formData && user.id === formData.userId;

  useEffect(() => {
    const fetchFormData = async () => {
      if (!formId) return;
      setIsLoading(true);
      try {
        const { data: form, error } = await supabase
          .from('forms')
          .select('*')
          .eq('id', formId)
          .maybeSingle();

        if (error || !form) {
          setFormData(null);
          return;
        }

        const formObj = form as Record<string, any>;
        const settings = (formObj.settings && typeof formObj.settings === 'object') ? formObj.settings : {};
        const transformedForm: FormData = {
          id: formObj.id,
          title: formObj.title,
          description: formObj.description || '',
          fields: Array.isArray(formObj.fields) ? (formObj.fields as unknown as FormField[]) : [],
          logoUrl: formObj.logo_url,
          brandColor: formObj.brand_color || '#00daf3',
          userId: formObj.user_id
        };
        
        setFormData(transformedForm);
      } catch (error) {
        console.error('Error fetching form:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFormData();
  }, [formId]);

  const handleInputChange = (fieldId: string, value: string) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formId || !formData) return;
    
    setIsSubmitting(true);
    try {
      const newSubmissionId = crypto.randomUUID();
      const { error } = await supabase.from('submissions').insert({
        id: newSubmissionId,
        form_id: formId,
        data: responses,
        user_agent: navigator.userAgent.toLowerCase(),
      });

      if (error) throw error;
      setIsSubmitted(true);
      toast({ title: "Form submitted!" });
    } catch (error) {
      console.error('Submission error:', error);
      toast({ title: "Submission failed", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-4">
        <div className="text-center space-y-6">
          <div className="relative">
             <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
             <div className="absolute inset-0 blur-xl bg-primary/20 animate-pulse" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground animate-pulse">Initializing Orchestration...</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6 text-center">
        <Card className="bg-surface-low/40 backdrop-blur-3xl border-none p-12 rounded-[3rem] max-w-md shadow-2xl">
          <div className="h-20 w-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-8">
             <MessageSquare className="h-10 w-10 text-destructive/40" />
          </div>
          <h2 className="text-3xl font-display font-bold mb-4">Module Inactive</h2>
          <p className="text-muted-foreground mb-10 leading-relaxed">The requested orchestration module is either offline or does not exist in our registry.</p>
          <Button onClick={() => window.location.href = '/'} variant="outline" className="rounded-2xl h-14 px-8 border-none bg-surface-high hover:bg-surface-highest">Return to Registry</Button>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-surface-lowest text-foreground font-sans selection:bg-primary/30">
        <Helmet>
          <title>Success | {formData.title}</title>
        </Helmet>
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay pointer-events-none" />
        </div>
        <div className="relative min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md animate-in zoom-in-95 fade-in duration-700">
            <Card className="border-none bg-surface-low/40 backdrop-blur-3xl rounded-[3rem] p-12 text-center shadow-2xl shadow-primary/10">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce-subtle">
                <CheckCircle className="h-12 w-12 text-primary" />
              </div>
              <h2 className="text-4xl font-display font-bold tracking-tightest mb-4">Success</h2>
              <p className="text-muted-foreground font-medium leading-relaxed mb-10">
                Your response has been securely synchronized with our AI orchestration engine.
              </p>
              <Button onClick={() => window.location.reload()} variant="ghost" className="rounded-2xl h-12 px-8 font-bold uppercase tracking-widest text-[10px] hover:bg-surface-high transition-all">
                Submit another response
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-lowest text-foreground font-sans selection:bg-primary/30">
      <Helmet>
        <title>{formData.title} | Bravax Forms</title>
      </Helmet>

      {/* Dynamic Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay pointer-events-none" />
      </div>

      {isOwner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-surface-highest/80 backdrop-blur-md border-b border-border/10 py-2.5 px-4 flex items-center justify-between gap-2 overflow-x-auto">
           <div className="flex items-center gap-2 shrink-0">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Preview Mode</span>
           </div>
           <div className="flex gap-2 shrink-0">
              <Button asChild variant="secondary" size="sm" className="h-8 rounded-xl px-3 gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-surface-low hover:bg-surface-high border border-border/5">
                 <Link to={`/form-builder/${formId}`}>
                    <Edit className="h-3 w-3" /> Edit
                 </Link>
              </Button>
              <Button asChild variant="default" size="sm" className="h-8 rounded-xl px-3 gap-1.5 text-[10px] font-bold uppercase tracking-widest shadow-md shadow-primary/20">
                 <Link to={`/forms/${formId}/submissions`}>
                    <BarChart3 className="h-3 w-3" /> Results
                 </Link>
              </Button>
           </div>
        </div>
      )}

      <div className="relative min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-12">
        <div className="w-full max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-1000">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12 space-y-3 sm:space-y-4">
             {formData.logoUrl && (
               <img src={formData.logoUrl} alt="Logo" className="h-10 sm:h-12 mx-auto mb-4 sm:mb-6 object-contain" />
             )}
             <div className="flex items-center justify-center gap-3 mb-3 sm:mb-4">
                <div className="h-px w-8 bg-primary/30" />
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full">Secure Form</span>
                <div className="h-px w-8 bg-primary/30" />
             </div>
             <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold tracking-tightest leading-tight">
               {formData.title}
             </h1>
             {formData.description && (
               <p className="text-base sm:text-lg text-muted-foreground font-medium max-w-lg mx-auto leading-relaxed">
                 {formData.description}
               </p>
             )}
          </div>

          {/* Form Card */}
          <Card className="border-none bg-surface-low/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl shadow-primary/5">
            <CardContent className="p-8 md:p-16">
              <form onSubmit={handleSubmit} className="space-y-12">
                {formData.fields.map((field) => (
                  <div key={field.id} className="space-y-4">
                    <div className="flex items-center justify-between ml-1">
                      <Label htmlFor={field.id} className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                        {field.label} {field.required && <span className="text-primary">*</span>}
                      </Label>
                    </div>
                    
                    {field.type === 'select' ? (
                      <Select onValueChange={(val) => handleInputChange(field.id, val)}>
                        <SelectTrigger className="h-16 bg-surface-low/50 border-none rounded-2xl px-6 text-base focus:ring-1 focus:ring-primary/20 shadow-inner transition-all hover:bg-surface-low">
                          <SelectValue placeholder={field.placeholder || "Select option..."} />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-highest border-border/10 rounded-2xl p-2">
                          {field.options?.map((opt) => (
                            <SelectItem key={opt} value={opt} className="rounded-xl p-3 focus:bg-primary/10">
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'textarea' ? (
                      <Textarea
                        id={field.id}
                        required={field.required}
                        placeholder={field.placeholder}
                        value={responses[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        className="min-h-[160px] bg-surface-low/50 border-none rounded-2xl px-6 py-4 text-base focus:ring-1 focus:ring-primary/20 shadow-inner transition-all resize-none hover:bg-surface-low"
                      />
                    ) : (
                      <Input
                        id={field.id}
                        type={field.type === 'number' ? 'number' : 'text'}
                        required={field.required}
                        placeholder={field.placeholder}
                        value={responses[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        className="h-16 bg-surface-low/50 border-none rounded-2xl px-6 text-base focus:ring-1 focus:ring-primary/20 shadow-inner transition-all hover:bg-surface-low"
                      />
                    )}
                  </div>
                ))}

                <div className="pt-8">
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full h-20 rounded-[1.5rem] btn-gradient text-xl font-bold shadow-2xl shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 gap-3"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span>Processing Submission...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Response</span>
                        <ArrowRight className="h-6 w-6" />
                      </>
                    )}
                  </Button>
                  <div className="mt-8 flex flex-col items-center gap-4">
                     <div className="flex items-center gap-2 px-4 py-2 bg-surface-low/50 rounded-full border border-border/5">
                        <Sparkles className="h-3 w-3 text-primary" />
                        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Secured by AI Architect</span>
                     </div>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FormView;